/**
 * DropSpotDock — HOARD-style side panel showing neighborhood flyers & events.
 * All neighborhoods listed, tapped one expanded first.
 * Each item shows flyer thumbnail as the "logo," event info text, tap to view full flyer.
 */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, ChevronDown, ChevronRight, Calendar, MapPin, ExternalLink, Image as ImageIcon, Play } from "lucide-react";
import { ViewItModal } from "../../radio/ViewIt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const abs = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export const DropSpotDock = ({ spots, activeSpotId, onClose }) => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(activeSpotId);
  const [flyerOpen, setFlyerOpen] = useState(null); // item with flyer in lightbox
  const [videoOpen, setVideoOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dropspots/all-items`)
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => { setExpanded(activeSpotId); }, [activeSpotId]);

  const toggle = useCallback((id) => setExpanded((e) => (e === id ? null : id)), []);

  // Group items by spot_id
  const grouped = {};
  for (const item of items) {
    if (!grouped[item.spot_id]) grouped[item.spot_id] = [];
    grouped[item.spot_id].push(item);
  }

  // Sort spots: active first, then alphabetically
  const sorted = [...spots].sort((a, b) => {
    if (a.spot_id === activeSpotId) return -1;
    if (b.spot_id === activeSpotId) return 1;
    return a.name.localeCompare(b.name);
  });

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1100] bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <aside
        className="fixed z-[1101] bg-[#0C1117] text-white shadow-2xl flex flex-col
          left-0 right-0 bottom-0 max-h-[85vh] rounded-t-3xl border-t border-white/10
          sm:left-auto sm:right-0 sm:top-0 sm:bottom-0 sm:max-h-none sm:w-[400px] sm:rounded-none sm:border-l sm:border-t-0"
        data-testid="dropspot-dock"
      >
        {/* Handle bar (mobile) */}
        <div className="sm:hidden flex justify-center pt-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-3 pb-3 sm:pt-5 border-b border-white/[0.06]">
          <div>
            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-[#F2CC8F]/80">What's happening</p>
            <h2 className="font-['Playfair_Display'] text-lg leading-tight">It's Happening</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10 text-white/50 hover:text-white" data-testid="dropspot-dock-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Neighborhood list */}
        <div className="flex-1 overflow-y-auto" data-testid="dropspot-dock-list">
          {sorted.map((spot) => {
            const spotItems = grouped[spot.spot_id] || [];
            const isOpen = expanded === spot.spot_id;
            return (
              <div key={spot.spot_id} className="border-b border-white/[0.04]" data-testid={`dropspot-neighborhood-${spot.spot_id}`}>
                <button
                  type="button"
                  onClick={() => toggle(spot.spot_id)}
                  className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-white/[0.03] transition-colors"
                  data-testid={`dropspot-toggle-${spot.spot_id}`}
                >
                  <div className="flex items-center gap-2.5">
                    <MapPin className="h-4 w-4 text-[#7FE3D6]" />
                    <span className="font-semibold text-sm text-white/90">{spot.name}</span>
                    {spotItems.length > 0 && (
                      <span className="text-[10px] font-bold text-[#F2CC8F] bg-[#F2CC8F]/10 px-1.5 py-0.5 rounded-full">{spotItems.length}</span>
                    )}
                  </div>
                  {isOpen ? <ChevronDown className="h-4 w-4 text-white/30" /> : <ChevronRight className="h-4 w-4 text-white/30" />}
                </button>

                {isOpen && (
                  <div className="px-5 pb-4 space-y-2" data-testid={`dropspot-items-${spot.spot_id}`}>
                    {spotItems.length === 0 ? (
                      <p className="text-xs text-white/30 italic">Nothing posted here yet — check back soon.</p>
                    ) : (
                      spotItems.map((item) => (
                        <DropSpotItem key={item.item_id} item={item} onFlyerOpen={setFlyerOpen} onVideoOpen={setVideoOpen} />
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Flyer lightbox */}
      {flyerOpen && (
        <div className="fixed inset-0 z-[1200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setFlyerOpen(null)} data-testid="flyer-lightbox">
          <div className="max-w-lg w-full max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setFlyerOpen(null)} className="absolute -top-3 -right-3 z-10 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white" data-testid="flyer-lightbox-close">
              <X className="h-5 w-5" />
            </button>
            <img src={abs(flyerOpen.flyer_url || flyerOpen.media_url)} alt={flyerOpen.title} className="w-full rounded-2xl object-contain max-h-[85vh]" />
            <p className="text-center text-sm text-white/70 mt-2">{flyerOpen.title}</p>
          </div>
        </div>
      )}

      {/* Video lightbox */}
      {videoOpen && <ViewItModal media={{ url: videoOpen.media_url, embed_url: videoOpen.embed_url, type: videoOpen.embed_url ? "embed" : "video", title: videoOpen.title }} title={videoOpen.title} onClose={() => setVideoOpen(null)} />}
    </>,
    document.body
  );
};

const DropSpotItem = ({ item, onFlyerOpen, onVideoOpen }) => {
  const hasFlyer = item.flyer_url || (item.media_type === "image" && item.media_url);
  const hasVideo = item.media_type === "video" && (item.media_url || item.embed_url);
  const flyerSrc = hasFlyer ? abs(item.flyer_url || item.media_url) : null;

  return (
    <div
      className="flex gap-3 rounded-xl bg-white/[0.04] border border-white/[0.06] p-2.5 hover:bg-white/[0.06] transition-colors group"
      data-testid={`dropspot-item-${item.item_id}`}
    >
      {/* Flyer thumbnail */}
      {flyerSrc ? (
        <button
          type="button"
          onClick={() => onFlyerOpen(item)}
          className="h-16 w-16 rounded-lg overflow-hidden shrink-0 bg-white/5 hover:ring-2 hover:ring-[#F2CC8F]/50 transition-all"
          title="View full flyer"
          data-testid={`dropspot-flyer-thumb-${item.item_id}`}
        >
          <img src={flyerSrc} alt="" className="h-full w-full object-cover" />
        </button>
      ) : hasVideo ? (
        <button
          type="button"
          onClick={() => onVideoOpen(item)}
          className="h-16 w-16 rounded-lg overflow-hidden shrink-0 bg-[#FF3B3B]/10 flex items-center justify-center hover:bg-[#FF3B3B]/20 transition-colors"
          title="Play video"
          data-testid={`dropspot-video-thumb-${item.item_id}`}
        >
          <Play className="h-6 w-6 text-[#FF3B3B]" />
        </button>
      ) : (
        <div className="h-16 w-16 rounded-lg shrink-0 bg-white/5 flex items-center justify-center">
          <Calendar className="h-5 w-5 text-white/20" />
        </div>
      )}

      {/* Info */}
      <div className="min-w-0 flex-1 flex flex-col justify-center">
        <p className="text-[13px] font-semibold text-white/90 leading-snug line-clamp-2">{item.title}</p>
        {item.event_date && (
          <p className="text-[10px] text-[#F2CC8F] font-bold mt-0.5 inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {item.event_date}{item.event_time ? ` · ${item.event_time}` : ""}
          </p>
        )}
        {item.venue && <p className="text-[10px] text-white/40 mt-0.5">{item.venue}</p>}
        {item.description && <p className="text-[10px] text-white/35 mt-0.5 line-clamp-2">{item.description}</p>}
        {item.link && (
          <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#7FE3D6] font-semibold inline-flex items-center gap-0.5 mt-1 hover:underline">
            More info <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
    </div>
  );
};
